import LogRocket from 'logrocket';

import FullStory from 'pmn/saas/fullstory';
import Mixpanel from 'pmn/saas/mixpanel';
import loadCookie from 'pmn/utils/cookie';
import { AppConfig, UserInfo } from 'pmn/types';
import { suggestifyEmails } from 'pmn/utils/emailSuggestor';
import { populateHiddenTimezoneField } from 'pmn/utils/signupEnhancements';

const addMixpanelIdToForms = (mixpanel: Mixpanel) => {
  const mixpanelHiddenInput = document.querySelector(
    'input[type="hidden"][name="mixpanel_id"]',
  );

  if (!mixpanelHiddenInput) {
    return;
  }

  (mixpanelHiddenInput as HTMLInputElement).value = mixpanel.getDistinctId();
};

const initLogRocket = ({ LOGROCKET_DSN, LOGROCKET_ENABLED }: AppConfig) => {
  if (LOGROCKET_ENABLED) {
    LogRocket.init(LOGROCKET_DSN);
  }
  return LogRocket;
};

const initFullstory = (appConfig: AppConfig, userInfo: UserInfo) => {
  const fullstory = new FullStory();
  fullstory.init(appConfig);
  fullstory.identify(userInfo);
  return fullstory;
};

const initMixpanel = (appConfig: AppConfig, userInfo: UserInfo) => {
  const mixpanel = new Mixpanel();
  mixpanel.init(appConfig, () => {
    mixpanel.identify(userInfo);
    mixpanel.page(userInfo);
    addMixpanelIdToForms(mixpanel);
  });
  return mixpanel;
};

export default class MainView {
  appConfig?: AppConfig;
  userInfo?: UserInfo;

  logrocket?: ReturnType<typeof initLogRocket>;
  fullstory?: ReturnType<typeof initFullstory>;
  mixpanel?: ReturnType<typeof initMixpanel>;

  mount(): void {
    const appConfig = loadCookie<AppConfig>('app_config');

    if (appConfig) {
      this.appConfig = appConfig;
      this.logrocket = initLogRocket(appConfig);
    }

    const userInfo = loadCookie<UserInfo>('current_user');

    if (userInfo) {
      this.userInfo = userInfo;
    }

    if (appConfig && userInfo) {
      this.fullstory = initFullstory(appConfig, userInfo);
      this.mixpanel = initMixpanel(appConfig, userInfo);
    }

    this.identifyLogRocket();
    this.maybeTrackJoinButtons();

    // in case we have signup.modal enabled
    populateHiddenTimezoneField();

    suggestifyEmails();
  }

  // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-empty-function
  unmount(): void {}

  maybeTrackJoinButtons(): void {
    const joinButtons = document.querySelectorAll('[data-action="register"]');
    if (!joinButtons) {
      return;
    }
    for (let i = 0; i < joinButtons.length; i += 1) {
      const joinButton = joinButtons[i] as HTMLAnchorElement;

      this.trackLink(joinButton, 'Clicked to register', {
        position: joinButton.dataset
          ? joinButton.dataset.position
          : joinButton.getAttribute('data-position'),
      });
    }
  }

  track(event: string, additionalTraits = {}): void {
    const { mixpanel, trackingTraits } = this;
    if (!mixpanel) {
      console.warn('Mixpanel not initialized');
      return;
    }
    mixpanel.track(event, Object.assign({}, trackingTraits, additionalTraits));
  }

  trackLink(
    element: HTMLAnchorElement,
    event: string,
    additionalTraits = {},
  ): void {
    const { mixpanel, trackingTraits } = this;
    if (!mixpanel) {
      console.warn('Mixpanel not initialized');
      return;
    }
    const properties = Object.assign({}, trackingTraits, additionalTraits);
    mixpanel.trackLink(element, event, properties);
  }

  identifyLogRocket(): void {
    const { logrocket, userInfo } = this;

    if (!logrocket || !userInfo) {
      return;
    }

    const { id, traits } = userInfo;
    logrocket.identify(id, traits);
  }

  get trackingTraits(): UserInfo['traits'] & { page_url: string } {
    const { userInfo } = this;
    const { traits } = userInfo || {};
    return Object.assign({}, traits, { page_url: window.location.toString() });
  }

  trackShareButtons(type: string): void {
    const shareButtons = document.querySelectorAll('[data-share]');
    for (let i = 0; i < shareButtons.length; i += 1) {
      const button = shareButtons[i] as HTMLButtonElement;
      button.addEventListener('click', () => {
        if (button.dataset.share === 'facebook') {
          this.track('Clicked to share on facebook', { type });
        } else if (button.dataset.share === 'google') {
          this.track('Clicked to share on google', { type });
        } else if (button.dataset.share === 'twitter') {
          this.track('Clicked to share on twitter', { type });
        }
      });
    }
  }
}
